import { AddWorksheetOptions, Workbook, Worksheet } from 'exceljs'
import {
  getFilteredResultsFromContainers,
  getFilteredResultsFromProviders,
} from '../calculators/calculator-result-utility'
import { Provider } from '../calculators/providers/common/Provider'
import { SystemContainerCalculator } from '../calculators/SystemContainerCalculator'
import { VersionCalculator } from '../calculators/VersionCalculator'
import {
  buildingResultCategories,
  hybridHeatPumpResultCategories,
  localGridResultCategories,
  providerResultCategories,
  ResultCategory,
  versionResultCategories,
} from '../provider/result-categories'
import { energyPerYearUnit, powerUnit } from '../util/formatters'
import { hoursInYear } from '../util/hours-in-year'
import { ProviderType } from '../generated/graphql'
import { hybridHeatPumpExtraOutputProperties } from '../calculators/providers/HybridHeatPumpProvider'
import { EnergyBusCalculator } from '../calculators/EnergyBusCalculator'

type ColumnData = {
  index: number
  heading?: string
  name?: string
  values: number[]
  total: number | string
  max: number | string
}

type AddSheetData = {
  resultCategories: ResultCategory[]
  containers: SystemContainerCalculator[] | null
  energyBus: EnergyBusCalculator | undefined | null
  providers: Provider[] | null
}

const addDataToSheet = (
  sheet: Worksheet,
  data: AddSheetData,
  t: (key: string) => string,
) => {
  const { resultCategories, containers, providers, energyBus } = data

  let index = 1
  const columnData = resultCategories.flatMap((category) =>
    category.subCategories.map((subCategory, subCategoryIndex): ColumnData => {
      index += 1

      const results = containers
        ? getFilteredResultsFromContainers(subCategory, containers, energyBus)
        : providers && getFilteredResultsFromProviders(providers, subCategory)

      if (!results) {
        throw new Error('Incorrect input while adding data to sheet')
      }

      const isPower = subCategory.hybridHeatPumpExtraOutputKey
        ? hybridHeatPumpExtraOutputProperties[
            subCategory.hybridHeatPumpExtraOutputKey
          ].isPower
        : true

      const formatValue = (value: number) => (isPower ? value / 1000 : value)
      const formatTotal = (total: number) =>
        isPower ? total / (1000 * 1000) : NaN

      return {
        index,
        heading: subCategoryIndex === 0 ? t(category.nameKey) : undefined,
        name: t(subCategory.nameKey),
        values: results.values.map(formatValue),
        total: formatTotal(results.total),
        max: formatValue(results.max),
      }
    }),
  )

  // Hack first column data
  columnData.unshift({
    index: 1,
    values: Array.from(Array(hoursInYear).keys()).map(
      (hourIndex) => hourIndex + 1,
    ),
    total: `${t('common.total')} (${energyPerYearUnit})`,
    max: `${t('common.max')} (${powerUnit})`,
  })

  // Insert column data
  columnData.forEach((filter) => {
    // eslint-disable-next-line no-param-reassign
    sheet.getColumn(filter.index).values = filter.values
  })

  const headerRows = [
    columnData.map((filter) => filter.heading),
    columnData.map((filter) => filter.name),
    columnData.map((filter) => filter.total),
    columnData.map((filter) => filter.max),
    [],
    [`${t('common.hour')} (${powerUnit})`],
  ]
  sheet.insertRows(1, headerRows)
}

/* eslint-disable no-param-reassign */
const applyStylesToSheet = (sheet: Worksheet) => {
  // Set default font.
  sheet.columns.forEach((column) => {
    column.font = {
      ...column.font,
      name: 'Calibri',
      size: 11,
    }
  })

  // Bold headers on first two rows.
  sheet.getRows(1, 2)?.forEach((row) =>
    row.eachCell((cell) => {
      cell.style.font = {
        ...cell.style.font,
        bold: true,
      }
    }),
  )

  // Bold additional headers in first column.
  const additionalHeaderCells = [
    sheet.getCell('A3'),
    sheet.getCell('A4'),
    sheet.getCell('A6'),
  ]
  additionalHeaderCells.forEach((cell) => {
    cell.style.font = {
      ...cell.style.font,
      bold: true,
    }
  })

  // One decimal for single values.
  sheet.columns.slice(1).forEach((column) => {
    column.numFmt = '#,##0.0'
  })

  // One decimal for total and max values.
  sheet.getRows(3, 2)?.forEach((row) => {
    row.numFmt = '#,##0.0'
  })

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  const pixelsPerExcelWidthUnit = 7
  const paddingInPixels = 5

  const estimateCellWidth = (location: string) => {
    const cell = sheet.getCell(location)
    const content = cell.toString()

    if (!context || !cell.font?.size || !cell.font?.name) {
      return content.length
    }

    context.font = `${cell.font.size}pt ${cell.font.name}, sans-serif`

    return (
      (context.measureText(content).width + paddingInPixels) /
      pixelsPerExcelWidthUnit
    )
  }

  // Set first column width.
  sheet.getColumn('A').width = Math.max(
    estimateCellWidth('A1'),
    estimateCellWidth('A3'),
  )

  // Set column widths based on header width.
  sheet.columns.slice(1).forEach((column) => {
    column.width = estimateCellWidth(`${column.letter ?? 'A'}2`)
  })
}
/* eslint-enable no-param-reassign */

const WORKSHEET_NAME_MAX_LENGTH = 31

/** Worksheet name cannot include any of the following characters: * ? : \ / [ ] */
export const sanitizeWorksheetName = (name: string): string =>
  name.replace(/[*?:\\/[\]]/g, '').slice(0, WORKSHEET_NAME_MAX_LENGTH)

export function createVersionExport(
  calculator: VersionCalculator,
  providerIds: string[],
  containerIdsToNames: Record<string, string>,
  t: (key: string) => string,
): Workbook {
  const usedWorksheetNames = new Set<string>()

  const workbook = new Workbook()
  workbook.creator = 'Sweco ENEX'
  workbook.created = new Date()

  const getUniqueWorksheetName = (name: string): string => {
    const sanitizedName = sanitizeWorksheetName(name)

    if (!usedWorksheetNames.has(sanitizedName)) {
      return sanitizedName
    }

    for (let i = 0; i < 1000; i++) {
      const postfix = `-${i}`
      const nameSuggestion = `${sanitizedName.slice(
        0,
        WORKSHEET_NAME_MAX_LENGTH - postfix.length,
      )}${postfix}`

      if (!usedWorksheetNames.has(nameSuggestion)) {
        return nameSuggestion
      }
    }

    throw new Error(`Unable to produce unique worksheet name for ${name}`)
  }

  const options: Partial<AddWorksheetOptions> = {
    // Freeze first two rows.
    views: [{ state: 'frozen', ySplit: 4 }],
  }

  const addWorksheet = (name: string, data: AddSheetData) => {
    const uniqueName = getUniqueWorksheetName(name)

    usedWorksheetNames.add(uniqueName)

    const worksheet = workbook.addWorksheet(uniqueName, options)

    addDataToSheet(worksheet, data, t)

    const nameCell = worksheet.getCell('A1')
    nameCell.value = name

    applyStylesToSheet(worksheet)
  }

  // Version sheet
  addWorksheet(t('export.version.sheetName'), {
    resultCategories: versionResultCategories,
    containers: calculator.getAllContainers(),
    energyBus: calculator.findEnergyBus(),
    providers: null,
  })

  const filterContainersBySelectedProviders = (
    container: SystemContainerCalculator,
  ) =>
    container
      .getProviders()
      .some((provider) => providerIds.includes(provider.id))

  const addContainerSheetsUsing =
    (containerResultCategories: ResultCategory[]) =>
    (container: SystemContainerCalculator) => {
      const containerName = containerIdsToNames[container.id]

      // Container sheet
      addWorksheet(containerName, {
        resultCategories: containerResultCategories,
        containers: [container],
        energyBus: null,
        providers: null,
      })

      // Provider sheets
      const providers = container
        .getProviders()
        .filter((provider) => providerIds.includes(provider.id))

      providers.forEach((provider) => {
        const providerName = provider.name
        const name = `${containerName}|${providerName}`

        const resultCategories =
          provider.type === ProviderType.HybridHeatPump
            ? hybridHeatPumpResultCategories
            : providerResultCategories

        addWorksheet(name, {
          resultCategories,
          containers: null,
          energyBus: null,
          providers: [provider],
        })
      })
    }

  calculator
    .getNonMultipliedBuildings()
    .filter(filterContainersBySelectedProviders)
    .forEach(addContainerSheetsUsing(buildingResultCategories))

  calculator
    .getLocalGrids()
    .filter(filterContainersBySelectedProviders)
    .forEach(addContainerSheetsUsing(localGridResultCategories))

  return workbook
}
